// @ts-nocheck
import React, { useState, useEffect, useRef, isValidElement } from "react";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useSortBy,
  useAsyncDebounce,
} from "react-table";
import cn from "classnames";

import "./global-table.scss";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  placeholder,
  label = "Search",
  onValueChange,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  useEffect(() => {
    setValue(globalFilter);
  }, [globalFilter]);

  return (
    <div className="search-block">
      <span>{label}: </span>
      <input
        value={value || ""}
        onChange={(e) => {
          const next = e.target.value;
          setValue(next);
          onValueChange?.(next);
          onChange(next);
        }}
        placeholder={placeholder ?? `${count} records...`}
      />
    </div>
  );
}

function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <select
      className="select-filter-block"
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

const toSearchText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (isValidElement(value)) return "";
  return "";
};

const buildGlobalFilterFn = (allowedColumnIds) => {
  const normalizedAllowed =
    Array.isArray(allowedColumnIds) && allowedColumnIds.length > 0
      ? new Set(allowedColumnIds)
      : null;

  return (rows, columnIds, filterValue) => {
    const raw = (filterValue ?? "").toString().trim().toLowerCase();
    if (!raw) return rows;

    const idsToSearch = normalizedAllowed
      ? columnIds.filter((id) => normalizedAllowed.has(id))
      : columnIds;

    return rows.filter((row) => {
      for (const id of idsToSearch) {
        if (id === "checked" || id === "starred") continue;
        const text = toSearchText(row.values?.[id]).toLowerCase();
        if (text.includes(raw)) return true;
      }
      return false;
    });
  };
};

const TableComponent = ({
  data,
  isLoading = false,
  dateColumns,
  formattingForDateColumns,
  showSearch,
  searchPlaceholder,
  searchLabel,
  globalFilterColumnIds,
  onGlobalFilterValueChange,
  onFilteredRowsChange,
  globalFilterResetKey,
  pauseDataUpdatesWhileFiltering = true,
  checkboxColumsHandler,
  showRowOverlay,
  checkedItems,
  disabledCheckbox,
  itemsWithSelectFilter = "",
  itemsToUseSorting, //must be an array
  setCellClassName = () => {},
}) => {
  // const [starredItems, setStarredItems] = useState([]);

  // const handleStarChange = (rowId) => {
  //   setStarredItems((prev) => {
  //     const currentIndex = prev.indexOf(rowId);
  //     if (currentIndex === -1) {
  //       // Not currently starred, add it
  //       return [...prev, rowId];
  //     } else {
  //       // Currently starred, remove it
  //       return prev.filter((id) => id !== rowId);
  //     }
  //   });
  // };

  //column structure
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [dataSnapshot, setDataSnapshot] = useState(data);
  const latestDataRef = useRef(data);
  latestDataRef.current = data;
  const prevResetKeyRef = useRef(globalFilterResetKey);

  useEffect(() => {
    if (!pauseDataUpdatesWhileFiltering) return;
    if (!isSearchActive) {
      setDataSnapshot(latestDataRef.current);
    }
  }, [isSearchActive, pauseDataUpdatesWhileFiltering, data]);

  const tableData =
    pauseDataUpdatesWhileFiltering && isSearchActive ? dataSnapshot : data;

  const columns = React.useMemo(() => {
    const handleCheckboxChange = (row) => {
      checkboxColumsHandler(row);
    };

    const CheckboxCell = ({ row }) => {
      return (
        <label className="main-checkbox">
          <input
            checked={checkedItems?.some((i) => i.original?.traderId === row.original?.traderId)}
            onChange={() => handleCheckboxChange(row)}
            type="checkbox"
            disabled={isValidElement(row.original.RequestedMarketData)}// TODO: change to something not defined (now works only for MD RequestedMarketData)
          />
          <span className="checkmark"></span>
        </label>
      );
    };

    // const StarCell = ({ row }) => {
    //   return (
    //     <div className="star-column-item" onClick={() => handleStarChange(row.id)}>
    //       {starredItems.includes(row.id) ? "★" : "☆"}{" "}
    //       {/* This can be replaced with images or icons */}
    //     </div>
    //   );
    // };

    const checkboxColumn = {
      Header: "",
      accessor: "checked",
      Cell: CheckboxCell,
    };

    // const starColumn = {
    //   Header: "",
    //   accessor: "starred",
    //   Cell: StarCell,
    // };

    const otherColumns = tableData.length
      ? Object.keys(tableData[0])
          .filter(
            (key) =>
              key !== "checked" && key !== "starred" && key !== "hiddenData"
          ) // Avoid duplicate accessors
          .map((item) => {
            let itemIsSelectFilter = false;
            let filterObject = null;
            if (Array.isArray(itemsWithSelectFilter)) {
              itemIsSelectFilter = itemsWithSelectFilter.includes(
                item.toLowerCase()
              );
            } else {
              itemIsSelectFilter = item.toLowerCase() === itemsWithSelectFilter;
            }
            if (itemIsSelectFilter) {
              filterObject = {
                Header: item,
                accessor: item,
                filter: "includes",
                Filter: SelectColumnFilter,
              };
            } else {
              let accessor = item;

              filterObject = {
                Header: item,
                accessor,
              };

              if (dateColumns) {
                filterObject.formatCell = dateColumns.includes(accessor) // format cell if column in dateColumns
                  ? formattingForDateColumns
                  : undefined;
              }

              if (dateColumns && dateColumns.includes(accessor)) {
                // you only use this sort method for columns with dates, when the dateColumns prop is passed
                filterObject.sortType = (rowA, rowB) => {
                  let dateA = new Date(rowA.values[accessor]);
                  let dateB = new Date(rowB.values[accessor]);
                  return dateA > dateB ? 1 : -1;
                };
              }
            }
            return filterObject;
          })
      : null;

    let columsToReturn = [...otherColumns];

    // if (starColumsHandler) {
    //   columsToReturn = [starColumn, ...columsToReturn];
    // }
    if (checkboxColumsHandler) {
      columsToReturn = [checkboxColumn, ...columsToReturn];
    }
    return [
      {
        Header: "none",
        columns: columsToReturn,
      },
    ];
  }, [
    tableData,
    itemsWithSelectFilter,
    checkedItems,
    formattingForDateColumns,
    dateColumns,
    checkboxColumsHandler,
  ]);
  // Use the state and functions returned from useTable to build your UI

  const [sortBy, setSortBy] = useState([{ id: "id", desc: true }]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: tableData,
      disableSortRemove: true,
      initialState: { sortBy },
      globalFilter: buildGlobalFilterFn(globalFilterColumnIds),
    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useSortBy // usesorting!
  );

  useEffect(() => {
    if (prevResetKeyRef.current === globalFilterResetKey) return;
    prevResetKeyRef.current = globalFilterResetKey;
    setGlobalFilter(undefined);
    setIsSearchActive(false);
  }, [globalFilterResetKey, setGlobalFilter]);

  useEffect(() => {
    setSortBy(state.sortBy);
  }, [state.sortBy]);

  useEffect(() => {
    onFilteredRowsChange?.({
      filteredCount: rows.length,
      totalCount: preGlobalFilteredRows.length,
      isFiltering: isSearchActive,
    });
  }, [rows.length, preGlobalFilteredRows.length, isSearchActive, onFilteredRowsChange]);

  const renderNoDataBlock = () => {
    if (data.at(-1) === "no-data") {
      return (
        <div className="account-overview-no-data-block">
          <span>No active requests</span>
        </div>
      );
    }
  };

  const setItemsToUseSorting = (currentColumn) => {
    if (itemsToUseSorting.length > 0 && data.length > 1) {
      const arrayToCheck = [
        ...itemsToUseSorting.map((item) => item.toLowerCase()),
      ];
      if (arrayToCheck.includes(currentColumn.Header.toLowerCase())) {
        return currentColumn.getSortByToggleProps();
      }
    }
  };

  const renderLoadingBackground = () => {
    if (isLoading) {
      return <div className="table-loading-bg" />;
    }
  };

  // Render the UI for your table
  return (
    <>
      {showSearch && (
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          placeholder={searchPlaceholder}
          label={searchLabel}
          onValueChange={(value) => {
            const active = (value ?? "").toString().trim().length > 0;
            setIsSearchActive(active);
            onGlobalFilterValueChange?.(value);
          }}
        />
      )}
      <table {...getTableProps()} className="table-wrapper">
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={i}>
              {headerGroup.headers.map((column, ind) => {
                return (
                  <th key={ind}>
                    <div
                      className="column-title"
                      {...column.getHeaderProps(
                        itemsToUseSorting
                          ? setItemsToUseSorting(column)
                          : undefined
                      )}
                    >
                      {column.isSorted && (
                        <span
                          className={cn({
                            ascending: column.isSortedDesc,
                            descending: !column.isSortedDesc,
                          })}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/icons/arrow.svg`}
                            alt="arrow"
                          />
                        </span>
                      )}
                      <span>{column.render("Header")}</span>
                      {/* Render the columns filter UI */}
                    </div>
                    {column.filter && <div>{column.render("Filter")}</div>}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody
          {...getTableBodyProps()}
          className={cn({
            hide: data.at(-1) === "no-data",
          })}
        >
          {rows?.at(-1)?.original !== "no-data" &&
            rows.map((row, i, array) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={i}>
                  {row.cells.map((cell, ind) => {
                    const value = cell.column.formatCell
                      ? cell.column.formatCell(cell.value)
                      : cell.render("Cell");
                    return (
                      <td
                        key={ind}
                        className={cn({
                          [setCellClassName(cell)]: setCellClassName(cell),
                        })}
                        {...cell.getCellProps()}
                      >
                        {value}
                      </td>
                    );
                  })}
                  {showRowOverlay &&
                    showRowOverlay(row, i === array.length - 1)}
                </tr>
              );
            })}
        </tbody>
        {renderLoadingBackground()}
      </table>
      {renderNoDataBlock()}
    </>
  );
};

export default React.memo(TableComponent);
